import { i18n } from '@/plugins/i18n';

/**
 * Object used to calculate one or multiple teams' vitality during a period of time.
 * Each VitalityCriterion will be put in a VitalityCategory constant.
 * Each VitalityCriterion object has a 'score' function to allow calculating a score between 0 and 100
 *    according to a given criterion.
 * Each VitalityCriterion also has a 'weight' attribute => When calculating the score of a VitalityCategory,
 *    an average will be calculated according to the scores of each of its criteria. This average must be weighted
 *    as not all criteria have the same impact on the 'vitality' of a team.
 * Finally, VitalityCriterion features a 'sentence' method which will create a human-readable sentence
 *    if the criterion is selected as the best (or worst) element of the period
 */
class VitalityCriterion {

  constructor(props) {
    this.titleTr = props.titleTr || '';
    this.key = props.key || '';
    this.weight = props.weight || 0;
    this.defaultChartIndex = props.defaultChartIndex || 0;
    // Override score function
    if (typeof props.score === 'function') {
      this.score = props.score;
    }
    // Override text function
    if (typeof props.text === 'function') {
      this.text = props.text;
    }
    // Override senseiHint
    if (typeof props.getSenseiHint === 'function') {
      this.getSenseiHint = props.getSenseiHint;
    }
    if (typeof props.chartData === 'function') {
      // Use custom chart configuration and datasets
      this.chartData = props.chartData;
    }
    if (typeof props.chartOptions === 'function') {
      // Use custom chart options
      this.chartOptions = props.chartOptions;
    }
    if (typeof props.chartNames === 'function') {
      this.chartNames = props.chartNames;
    }
  }

  /**
   * Calculate the score of a specific set of data according to criterion instance
   * This method must be redefined in each instance.
   * @param  {Array} data - Set of data
   * @param  {Object} range - selected period { start: Moment, end: Moment}
   * @param  {Object} additionnalParams - Any additionnal params, such as 'teams', 'lastRange', 'ranges'
   * @return {Object} - {
   *   score: From 0 to 100,
   *   data: {
   *     value: Any number,
   *     noTopsFlops: true if this data shouldn't appear in tops and flops
   *   }
   * }
   */
  score(data, range, additionnalParams) {
    return { score: 0, data: { value: 0, noTopsFlops: false } };
  }

  /**
   * Return an array of scores
   * @param  {Array} data - Set of data
   * @param  {Array} ranges - Array of { start: Moment, end: Moment}
   * @param  {Number} nVal - Number of scores to return
   * @param  {Array} teams - for debug purposes. teams concerned by the 'score' call
   * @return {Array} - Array of { score: From 0 to 100, value: Any number }
   */
  scores(data, ranges, nVal, teams) {
    const scores = [];
    for (let i = 0; i < nVal; i++) {
      if (ranges[i] && ranges[i + 1]) {
        const additionnalParams = {
          lastRange: ranges[i + 1],
          teams,
          ranges,
          currentRangeIndex: i,
        };
        scores.push(this.score(data, ranges[i], additionnalParams));
      }
    }
    return scores;
  }

  /**
   * Creates and returns a human-readable text string about the value
   * @param  {Object} data - 'data' returned from score function
   * @return {String} - Human-readable string
   */
  text(data) {
    return '';
  }

  /**
   * Get the text hint related to this criterion
   * @return {String} Hint to display
   */
  senseiHint() {
    const trKey = `sensei.${this.titleTr}`;
    const tr = i18n.t(trKey);
    return trKey === tr ? '' : tr;
  }

  /**
   * Title of the sensei hint (above)
   * @return {String} Title to display
   */
  senseiHintTitle() {
    const trKey = `sensei.${this.titleTr}_TITLE`;
    const tr = i18n.t(trKey);
    return trKey === tr ? '' : tr;
  }

  /**
   * Function to override to customize the sensei hint used for this criterion
   * @return {String} Text to use
   */
  getSenseiHint() {
    return false;
  }

  /**
   * Override this method to make custom datasets for the chart of this criterion
   * NOTE: If this returns an array, the index of each labels-datasets-object
   * should match its corresponding chartOptions and chartNames item
   * @param  {Array} teamGroups - Array of team groups built in the vitality getters
   * @return {Object|Array} - Object or array of objects containing 'dataets' and 'labels' property
   */
  chartData(teamGroups) {
    return false;
  }

  /**
   * Refer to Chart.js docs
   * NOTE: If this returns an array, the index of each options-object
   * should match its corresponding chartData and chartNames item
   * @return {Object|Array} Object or array of objects
   */
  chartOptions() {
    return false;
  }

  /**
   * Name of the chart (so the user may switch by clicking the chart name)
   * NOTE: If this returns an array, the index of each chart name
   * should match its corresponding chartData and chartOptions item
   * @return {String|Array} String or array of strings
   */
  chartNames() {
    return false;
  }

  get scoresKey() {
    return `${this.key}Scores`;
  }
}

export default VitalityCriterion;
